import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useToast } from "../components/ui/Toast";
import PromptForm, { PromptFormRef } from './PromptForm';
import { useNavigation } from '../contexts/NavigationContext';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { Spinner } from "../components/ui/Spinner";
import { ApiError, usePromptsApi } from '../apis/prompts-apis';
import { Prompt } from '../types';

export const EditPromptPage: React.FC = () => {
  const formRef = useRef<PromptFormRef>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { addToast } = useToast();
  const initialFetchMade = useRef(false);
  
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [originalPrompt, setOriginalPrompt] = useState<Prompt | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [existingPrompts, setExistingPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  
  const promptsApi = usePromptsApi();
  
  const { 
    showNavigationWarning, 
    pendingNavigation, 
    setShowNavigationWarning, 
    setPendingNavigation 
  } = useNavigation();

  const hasUnsavedChanges = !isReadOnly && 
    JSON.stringify(prompt) !== JSON.stringify(originalPrompt);

  // Handle browser back button
  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    if (hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  }, [hasUnsavedChanges]);

  // Set up block for browser back button
  useEffect(() => {
    // Add extra history entry when editing starts
    if (hasUnsavedChanges) {
      window.history.pushState(null, '', window.location.pathname);
    }

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges) {
        // Push state back to prevent navigation
        window.history.pushState(null, '', window.location.pathname);
        // Show warning dialog for back button
        setShowNavigationWarning(true);
        setPendingNavigation('/admin');
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, handleBeforeUnload, setShowNavigationWarning, setPendingNavigation]);

  useEffect(() => {
    const fetchData = async () => {
      if (id && !initialFetchMade.current) {
        initialFetchMade.current = true;
        setLoading(true);
        try {
          const getAllPrompts = true;
          const [promptData, allPrompts] = await Promise.all([
            promptsApi.getPrompt(id),
            promptsApi.getPrompts(getAllPrompts)
          ]);
          setPrompt(promptData);
          setOriginalPrompt(promptData);
          setExistingPrompts(allPrompts);
        } catch (error: any) {
          console.error('Error fetching data:', error);
          
          if (error instanceof ApiError && error.status === 403) {
            navigate('/admin/unauthorized');
            return;
          }
          
          // For all other errors, show the specific error message or fallback
          const errorMessage = error instanceof ApiError 
            ? error.message 
            : "Failed to fetch prompt details";
            
          addToast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
            duration: 5000
          });
          navigate('/admin');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [id, navigate, addToast]);

  const handleSubmit = async () => {
    if (!prompt) return;

    const isValid = formRef.current?.validateForm();
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
        duration: 4000
      });
      return;
    }

    try {
      await promptsApi.updatePrompt(prompt);
      const savedPrompt = { ...prompt };
      setOriginalPrompt(savedPrompt);
      setPrompt(savedPrompt);
      setIsReadOnly(true);
      
      addToast({
        title: "Success",
        description: "Prompt has been successfully updated",
        variant: "default",
        duration: 4000
      });

      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
      addToast({
        title: "Error",
        description: "Failed to update prompt: " + String(error),
        variant: "destructive",
        duration: 8000
      });
    }
  };

  const handleDiscardChanges = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation(null);
    } else {
      setPrompt(originalPrompt);
      setIsReadOnly(true);
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    setPrompt(originalPrompt);
    setIsReadOnly(true);
    
    if (pendingNavigation) {
      // Go back twice to handle the extra history entry we added
      window.history.go(-2);
      setPendingNavigation(null);
    } else {
      navigate('/admin');
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/admin');
    } else {
      navigate('/admin');
    }
  };

  if (loading || !prompt) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-blue-500" />
        <p className="text-center text-muted-foreground">Loading Prompt...</p>
      </div>
    );
  }

  return (
    <>
      <PromptForm
        ref={formRef}
        prompt={prompt}
        originalPrompt={originalPrompt}
        onPromptChange={setPrompt}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={true}
        readOnly={isReadOnly}
        onEditClick={() => setIsReadOnly(false)}
        onDiscardChanges={handleDiscardChanges}
        hasUnsavedChanges={hasUnsavedChanges}
        setIsReadOnly={setIsReadOnly}
        tagSuggestions={[]}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default EditPromptPage;